import { Pipe, PipeTransform } from '@angular/core';
import {
  Observable,
  asyncScheduler,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  fromEvent,
  map,
  merge,
  observeOn,
  of,
  retry,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { WallDecorRenderPreviewConfig } from '../../../../../photographer-client/src/app/modules/private-sessions/graphql';

import {
  AvailableWallDecorScenesGQL,
  WallDecorPieceForDisplayType,
} from '@ui/look-book-front/wall-decor-front/graphql';
import { LookBookProductType } from '@ui/look-book-front/types';
import { LookBookProductPreviewsService } from '@ui/look-book-front/look-book-product-previews.service';
import { groupArrayByIndex } from '../../../../../../helpers/array';
import { pickImagesForThumb } from 'graphql/libs/look-book-client/src/helpers';
import { ImagesInWallDecorMap } from '@ui/look-book-front/wall-decor-front/types';
import { environment } from '@ui/look-book-front/enviroment/enviroment';
import { LookBookCanvasSceneRenderBody } from 'graphql/libs/look-book-rendering/types';
import { HttpClient } from '@angular/common/http';
import cuid from 'cuid';
import { FaceDetectorService } from 'ZalamoLibrary/src/lib/zalamo/face-detection/face-detector.service';

const cache = new Map<string, any>();
const emptyCache = new Map<string, any>();

@Pipe({
  name: 'lookBookSingleWallDecorPreview',
  standalone: false,
})
export class LookBookSingleWallDecorPreviewPipe implements PipeTransform {
  availableScenes$ = this.availableWallDecorScenesGQL
    .fetch({})
    .pipe(
      map(
        ({ data: { getAvailableWallDecorScenes } }) =>
          getAvailableWallDecorScenes,
      ),
    );

  constructor(
    private availableWallDecorScenesGQL: AvailableWallDecorScenesGQL,
    private lookBookProductPreviewsService: LookBookProductPreviewsService,
    private httpClient: HttpClient,
    private faceDetectorService: FaceDetectorService,
  ) {}

  transform(
    photoId: string,
    allImages: {
      photoId: string;
      width: number;
      height: number;
      photoGroupId: string;
    }[] = [],
    productType = WallDecorPieceForDisplayType.Canvas,
    preparedCrop = null,
  ): Observable<WallDecorRenderPreviewConfig> {
    const availableImages = [photoId];
    const indexedPhotos = groupArrayByIndex(allImages, 'photoId');
    console.log(productType);
    return combineLatest([
      this.availableScenes$,
      combineLatest(
        availableImages.map((photoId) =>
          this.faceDetectorService.detectFacesByPhotoId(photoId),
        ),
      ),
      merge(
        fromEvent(window, 'storage').pipe(
          tap((x) => console.log(x)),
          filter((event: StorageEvent) => event.key === 'wallColor'),
          map((event: StorageEvent) => event.newValue),
        ),
        timer(0, 500).pipe(
          map(() => localStorage.wallColor ?? '#FFFFFF'),
          distinctUntilChanged(),
        ),
      ),
    ]).pipe(
      first(),
      switchMap(([scenes, faces, color]) => {
        preparedCrop = faces.length ? faces[0].maxSquare : null;
        const filtered = scenes.filter(
          (scene) =>
            (productType === WallDecorPieceForDisplayType.ZalamoCalendar &&
              scene.labs.includes('zalamo')) ||
            (scene.labs.includes(
              this.lookBookProductPreviewsService.labs[
                productType === WallDecorPieceForDisplayType.Canvas
                  ? LookBookProductType.WellDecor
                  : LookBookProductType.Tambour
              ],
            ) &&
              scene.pieces.some(
                ({ type }) =>
                  type ===
                  (productType === WallDecorPieceForDisplayType.Canvas
                    ? WallDecorPieceForDisplayType.Canvas
                    : WallDecorPieceForDisplayType.Tambour),
              )),
        );
        const pickedScene = pickImagesForThumb(
          allImages.filter(({ photoId }) => availableImages.includes(photoId)),
          filtered,
          1,
        );
        console.log('picked scene', pickedScene);
        const indexedPieces = groupArrayByIndex(
          pickedScene.matchingScene.pieces,
          'WallDecorPieceForDisplayId',
        );
        // @ts-ignore
        const images = Object.fromEntries(
          Object.entries(pickedScene.pieces).map(([index, value]) => {
            const imageRatio =
              indexedPhotos[value.photoId].width /
              indexedPhotos[value.photoId].height;

            const currentValue =
              indexedPieces[value.WallDecorPieceForDisplayId];
            const containerRatio = currentValue.flip
              ? currentValue.imageHeight / currentValue.imageWidth
              : currentValue.imageWidth / currentValue.imageHeight;

            return [
              index,
              {
                ...value,
                photoGroupId: indexedPhotos[value.photoId].photoGroupId,
                isFlip: value.isFlip,
                crop:
                  containerRatio === 1 &&
                  preparedCrop !== null &&
                  typeof preparedCrop === 'object' &&
                  Object.keys(preparedCrop).length === 4
                    ? preparedCrop
                    : containerRatio > imageRatio
                      ? {
                          w: 100,
                          x: 0,
                          h: (imageRatio / containerRatio) * 100,
                          y: (100 - (imageRatio / containerRatio) * 100) / 2,
                        }
                      : {
                          h: 100,
                          y: 0,
                          w: (containerRatio / imageRatio) * 100,
                          x: (100 - (containerRatio / imageRatio) * 100) / 2,
                        },
              },
            ];
          }),
        );
        return this.previewForWallDecor(
          pickedScene.matchingScene,
          images,
          color,
          productType === WallDecorPieceForDisplayType.ZalamoCalendar
            ? 1024
            : 720,
        ).pipe(
          catchError(() => {
            this.lookBookProductPreviewsService.nodeId = null;
            return this.previewForWallDecor(
              pickedScene.matchingScene,
              images,
              color,
            );
          }),
          retry(10),
          map((preview) => ({
            ...preview,
            wallDecorRenderPreviewConfigId: cuid(),
            color,
          })),
          shareReplay(1),
        );
      }),
    );
  }

  previewForWallDecor(
    scene,
    imageMap: ImagesInWallDecorMap,
    color,
    targetSize = 720,
  ) {
    const size = Math.min(
      1280,
      Math.ceil((targetSize * window.devicePixelRatio) / 80) * 80,
    );

    const body: LookBookCanvasSceneRenderBody & { isPreview: boolean } = {
      color,
      sceneId: scene.wallDecorSceneForDisplayId,
      nodeId: this.lookBookProductPreviewsService.nodeId,
      images: scene.pieces.reduce((prevValue, currentValue) => {
        if (imageMap[currentValue.WallDecorPieceForDisplayId]) {
          prevValue[currentValue.WallDecorPieceForDisplayId] =
            imageMap[currentValue.WallDecorPieceForDisplayId];
        }
        return prevValue;
      }, {} as ImagesInWallDecorMap),
      size,
      isPreview: true,
      pieces: [
        'front',
        ...scene.pieces.map(
          ({ WallDecorPieceForDisplayId }) => WallDecorPieceForDisplayId,
        ),
      ],
    };

    const index = JSON.stringify(body);

    // Jeśli mamy finalny render w cache – od razu zwróć i nic nie odpalaj.
    if (cache.has(index)) {
      return of(cache.get(index));
    }

    const emptyBody = {
      ...body,
      // Backend oczekuje pustej listy obrazków dla "empty" – zostawiamy tak jak było.
      images: [] as any,
    };
    const emptyIndex = JSON.stringify(emptyBody);

    console.log(body);

    type CanvasSceneResponse = {
      fileName: string;
      token: string;
      nodeId: string;
      ui: { overlayDom; overlayLayers; nodeId: string };
    };

    const toOutput = (
      v: {
        fileName: string;
        token: string;
      },
      isEmpty = false,
    ) => ({
      wallDecorSceneId: scene.wallDecorSceneForDisplayId,
      fileName: v.fileName,
      accessToken: v.token,
      images: isEmpty
        ? []
        : Object.entries(imageMap).map(
            ([wallDecorPieceId, { photoId, photoGroupId, crop, isFlip }]: [
              string,
              any,
            ]) => ({
              wallDecorPieceId,
              photoId,
              photoGroupId,
              crop,
              isFlip,
            }),
          ),
    });

    // DOCELOWY request (z obrazkami) – współdzielony między wielu subscriberów.
    const full$ = this.httpClient
      .post<CanvasSceneResponse>(environment.canvasScenesUrl, body, {
        responseType: 'json',
      })
      .pipe(
        tap((v) => {
          // utrzymujemy dotychczasowy side‑effect
          this.lookBookProductPreviewsService.nodeId = v.nodeId;
        }),
        map((v) => toOutput(v)),
        tap((out) => {
          cache.set(index, out);
        }),
        // zwracamy obiekt z cache, żeby utrzymać spójność zwracanej referencji
        map(() => cache.get(index)),
        shareReplay(1),
      );

    // PUSTY request (bez obrazków) albo szybki odczyt z emptyCache.
    const empty$ = emptyCache.has(emptyIndex)
      ? // Gdy mamy pusty w cache – emituj asynchronicznie, by "full$" miał szansę wygrać wyścig.
        of(emptyCache.get(emptyIndex)).pipe(observeOn(asyncScheduler))
      : this.httpClient
          .post<CanvasSceneResponse>(environment.canvasScenesUrl, emptyBody, {
            responseType: 'json',
          })
          .pipe(
            tap((v) => {
              // side‑effect zgodnie z poprzednią implementacją (dla świeżo renderowanego pustego)
              this.lookBookProductPreviewsService.nodeId = v.nodeId;
              if (!emptyCache.has(emptyIndex)) {
                emptyCache.set(emptyIndex, toOutput(v, true));
              }
            }),
            map(() => emptyCache.get(emptyIndex)),
          );

    // Równoległy start:
    // - jeśli "full$" wróci pierwszy → "takeUntil(full$)" przerwie empty$ i do strumienia pójdzie wyłącznie wynik docelowy
    // - jeśli "empty$" wróci pierwszy → emitujemy najpierw pusty, a potem docelowy
    return merge(empty$.pipe(takeUntil(full$)), full$);
  }
}
